import { AuthContext } from '@repo-level/core-front';
import { BonanzaContext } from 'contexts/bonanza';
import { ConfigContext } from 'contexts/config';
import { StardustRushContext } from 'contexts/stardustRush/stardustRush';
import { groupMember, vipGroups } from 'helpers/user';
import { useUserCanAccessQuestPromo } from 'modules/common/hooks';
import { usePromoRestrictions } from 'modules/common/hooks/usePromoRestrictions';
import { useAuroraCrystalAvailable } from 'modules/crystal/hooks';
import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { AuroraPanel } from './AuroraPanel';
import { BonanzaPromoSection } from './BonanzaSection';
import { MissionPanel } from './MissionPanel';
import { QuestPanel } from './QuestPanel';
import { StarDustRushPanel } from './StarDustRushPanel';
import { ScPromoPanels } from './styled';

interface PanelProps {
  onLinkClick?: () => void;
}

export const PromoPanelsDesktop = () => {
  const { isBonanzaPromoAvailable } = useContext(BonanzaContext);
  const { user, pendingUser } = useContext(AuthContext);
  const { isStardustRushAvailable, stardustRushCMSData } =
    useContext(StardustRushContext);
  const { isMissionDateValid } = useContext(ConfigContext);

  const { hasUserAccess } = usePromoRestrictions();

  const isVip = useMemo(
    () => vipGroups.some((group) => groupMember(group)(user)) && !pendingUser,
    [user, pendingUser]
  );

  const isStardustRush =
    isStardustRushAvailable && stardustRushCMSData && isVip;

  const { isAuroraCrystalVisible } = useAuroraCrystalAvailable();
  const isQuestPromoVisible = useUserCanAccessQuestPromo();
  const isMissionPromoVisible = isMissionDateValid && hasUserAccess;

  const isPanelsDesktop =
    isBonanzaPromoAvailable ||
    isStardustRush ||
    isAuroraCrystalVisible ||
    isQuestPromoVisible ||
    isMissionPromoVisible;

  if (!isPanelsDesktop) return null;

  return (
    <>
      <div className="divider" />

      <ScPromoPanels>
        {isBonanzaPromoAvailable && <BonanzaPromoSection />}
        {isStardustRush && <StarDustRushPanel />}
        {isAuroraCrystalVisible && <AuroraPanel />}
        {isQuestPromoVisible && <QuestPanel />}
        {isMissionPromoVisible && <MissionPanel />}
      </ScPromoPanels>
    </>
  );
};

export const PromoPanelsMobile: FC<PanelProps> = ({ onLinkClick }) => {
  const { isBonanzaPromoAvailable } = useContext(BonanzaContext);
  const { user, pendingUser } = useContext(AuthContext);
  const { isStardustRushAvailable } = useContext(StardustRushContext);
  const { isMissionDateValid } = useContext(ConfigContext);

  const { hasUserAccess } = usePromoRestrictions();

  const isVip = useMemo(
    () => vipGroups.some((group) => groupMember(group)(user)) && !pendingUser,
    [user, pendingUser]
  );

  const isStardustRush = isStardustRushAvailable && isVip;

  const { isAuroraCrystalVisible } = useAuroraCrystalAvailable();
  const isQuestPromoVisible = useUserCanAccessQuestPromo();
  const isMissionPromoVisible = isMissionDateValid && hasUserAccess;

  const isPanelsMobile =
    isBonanzaPromoAvailable ||
    isStardustRush ||
    isAuroraCrystalVisible ||
    isQuestPromoVisible ||
    isMissionPromoVisible;

  if (!isPanelsMobile) return null;

  return (
    <ScPromoPanels isMobile>
      {isBonanzaPromoAvailable && (
        <BonanzaPromoSection isMobile onLinkClick={onLinkClick} />
      )}
      {isStardustRush && (
        <StarDustRushPanel isMobile onLinkClick={onLinkClick} />
      )}
      {isAuroraCrystalVisible && <AuroraPanel onLinkClick={onLinkClick} />}
      {isQuestPromoVisible && <QuestPanel onLinkClick={onLinkClick} />}
      {isMissionPromoVisible && <MissionPanel onLinkClick={onLinkClick} />}
    </ScPromoPanels>
  );
};
